import type { IPeerConnection } from '../types';

export type PeerStats = {
  peerId: string;
  timestamp: number;
  bitrate: number;
  packetLoss: number;
  roundTripTime: number;
  framesPerSecond: number;
  jitter: number;
  connectionType: string;
  availableOutgoingBitrate?: number;
  frameWidth?: number;
  frameHeight?: number;
};

/**
 * PeerStatsManager - Collects and batches WebRTC stats from peer connections.
 *
 * Periodically polls RTCPeerConnection.getStats() and parses the report
 * into a normalized PeerStats structure. Stats are batched and can be
 * reported upstream.
 */
export class PeerStatsManager {
  private statsIntervals = new Map<string, ReturnType<typeof setInterval>>();
  private collections = new Map<string, { pending: boolean }>();
  private latestStats = new Map<string, PeerStats>();
  private previousOutboundVideo = new Map<string, { bytesSent: number; timestamp: number }>();
  private onStatsUpdated: ((stats: PeerStats) => void) | null = null;
  private pollIntervalMs: number;

  constructor(pollIntervalMs = 2000) {
    this.pollIntervalMs = pollIntervalMs;
  }

  public setOnStatsUpdated(callback: (stats: PeerStats) => void): void {
    this.onStatsUpdated = callback;
  }

  public startCollection(peerId: string, connection: IPeerConnection): void {
    this.stopCollection(peerId);
    const collection = { pending: false };
    this.collections.set(peerId, collection);
    const interval = setInterval(async () => {
      if (collection.pending) return;
      collection.pending = true;
      try {
        const stats = await this.collectStats(peerId, connection);
        if (stats && this.collections.get(peerId) === collection) {
          this.latestStats.set(peerId, stats);
          this.onStatsUpdated?.(stats);
        }
      } finally { collection.pending = false; }
    }, this.pollIntervalMs);

    this.statsIntervals.set(peerId, interval);
  }

  public stopCollection(peerId: string): void {
    this.collections.delete(peerId);
    const interval = this.statsIntervals.get(peerId);
    if (interval) {
      clearInterval(interval);
      this.statsIntervals.delete(peerId);
    }
    this.latestStats.delete(peerId);
    this.previousOutboundVideo.delete(peerId);
  }

  public getLatestStats(peerId: string): PeerStats | undefined {
    return this.latestStats.get(peerId);
  }

  public getAllStats(): PeerStats[] {
    return Array.from(this.latestStats.values());
  }

  public destroy(): void {
    for (const interval of this.statsIntervals.values()) {
      clearInterval(interval);
    }
    this.statsIntervals.clear();
    this.collections.clear();
    this.latestStats.clear();
    this.previousOutboundVideo.clear();
    this.onStatsUpdated = null;
  }

  private async collectStats(peerId: string, connection: IPeerConnection): Promise<PeerStats | null> {
    const rtcPc = connection.getRtcPeerConnection();
    if (!rtcPc) return null;

    try {
      const report = await rtcPc.getStats();
      return this.parseStatsReport(peerId, report);
    } catch (error) {
      console.warn(`[PeerStatsManager] Failed to collect stats for ${peerId}:`, error);
      return null;
    }
  }

  private parseStatsReport(peerId: string, report: RTCStatsReport): PeerStats {
    let bitrate = 0;
    let packetLoss = 0;
    let roundTripTime = 0;
    let framesPerSecond = 0;
    let jitter = 0;
    let connectionType = 'unknown';
    let availableOutgoingBitrate: number | undefined;
    let frameWidth: number | undefined;
    let frameHeight: number | undefined;
    let outboundBytesSent: number | null = null;
    let outboundTimestampMs: number | null = null;

    report.forEach((stats) => {
      if (stats.type === 'outbound-rtp' && stats.kind === 'video') {
        outboundBytesSent = (stats as any).bytesSent ?? null;
        outboundTimestampMs = typeof stats.timestamp === 'number' ? stats.timestamp : null;
        framesPerSecond = (stats as any).framesPerSecond ?? 0;
        frameWidth = (stats as any).frameWidth ?? frameWidth;
        frameHeight = (stats as any).frameHeight ?? frameHeight;
      }
      if (stats.type === 'remote-inbound-rtp') {
        packetLoss = (stats as any).packetsLost ?? 0;
        roundTripTime = (stats as any).roundTripTime ?? 0;
        jitter = (stats as any).jitter ?? 0;
      }
      if (stats.type === 'candidate-pair' && (stats as any).state === 'succeeded') {
        connectionType = (stats as any).remoteCandidateType ?? 'unknown';
        availableOutgoingBitrate = (stats as any).availableOutgoingBitrate ?? availableOutgoingBitrate;
      }
    });

    if (outboundBytesSent !== null && outboundTimestampMs !== null) {
      const previous = this.previousOutboundVideo.get(peerId);
      if (previous) {
        const bytesDelta = outboundBytesSent - previous.bytesSent;
        const timeDeltaSeconds = (outboundTimestampMs - previous.timestamp) / 1000;
        if (bytesDelta > 0 && timeDeltaSeconds > 0) {
          bitrate = Math.round((bytesDelta * 8) / timeDeltaSeconds);
        }
      }

      this.previousOutboundVideo.set(peerId, {
        bytesSent: outboundBytesSent,
        timestamp: outboundTimestampMs,
      });
    }

    return {
      peerId,
      timestamp: Date.now(),
      bitrate,
      packetLoss,
      roundTripTime,
      framesPerSecond,
      jitter,
      connectionType,
      availableOutgoingBitrate,
      frameWidth,
      frameHeight,
    };
  }
}
